import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { galleryImages } from '../../data/galleryImages';
import GalleryImage from '../../components/Gallery/GalleryImage';
import GalleryLightbox from '../../components/Gallery/GalleryLightbox';
import { useSEO } from '../../hooks/useSEO';

const GalleriesIndex = () => {
  const [lightbox, setLightbox] = useState({ isOpen: false, index: 0 });

  useSEO({
    title: 'Visual Gallery | Padua Weddings',
    description: 'A quiet walk through Padua. See the courtyard, candlelit rooms, and real celebration moments.'
  });

  const openLightbox = (index) => {
    setLightbox({ isOpen: true, index });
  };

  const closeLightbox = () => {
    setLightbox(prev => ({ ...prev, isOpen: false }));
  };

  const navigateLightbox = (direction) => {
    setLightbox(prev => ({
      ...prev,
      index: (prev.index + direction + galleryImages.length) % galleryImages.length
    }));
  };

  const heroImage = "https://ik.imagekit.io/aeu02xvjgn/Jae-Mhin-&-Colin.jpg?updatedAt=1782323354559";

  return (
    <div className="bg-brand-ivory min-h-screen">
      {/* 1. Clean Hero Section - Visual Focus only */}
      <section className="relative w-full overflow-hidden aspect-[4/5] md:aspect-video max-h-[85vh] bg-brand-espresso">
        <motion.div 
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full"
        >
          <img 
            src={heroImage} 
            alt="The Beauty of Padua" 
            className="w-full h-full object-cover select-none"
            loading="eager"
          />
        </motion.div>
      </section>

      {/* 2. Focused Intro Section */}
      <section className="max-w-4xl mx-auto px-6 py-24 md:py-36 text-center">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="flex flex-col items-center"
        >
          <h1 className="font-cinzel text-3xl md:text-5xl text-brand-espresso mb-8 leading-tight tracking-wide">
            Before you book a tour, <br className="hidden md:block" />
            take a quiet walk through Padua.
          </h1>
          
          <div className="h-px w-16 bg-brand-gold/40 mb-8" />
          
          <p className="font-sans text-brand-espresso/70 text-base md:text-lg max-w-2xl mb-12 leading-relaxed tracking-wide">
            See the courtyard, candlelit rooms, architectural details, and real celebration moments that make couples want to experience the venue in person.
          </p>
          
          <a 
            href="https://tour.paduaweddings.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block px-14 py-5 bg-brand-green text-white text-[10px] tracking-[0.4em] uppercase hover:bg-brand-espresso transition-all duration-500 shadow-sm"
          >
            See It in Person
          </a>
        </motion.div>
      </section>

      {/* 3. Masonry Gallery Grid */}
      <section className="max-w-[1920px] mx-auto px-4 md:px-12 pb-32">
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8 lg:gap-10">
          {galleryImages.map((image, idx) => (
            <GalleryImage 
              key={`${image.src}-${idx}`} 
              image={image} 
              index={idx} 
              onClick={openLightbox} 
            />
          ))}
        </div>
        
        {/* Subtle Bottom CTA */}
        <div className="mt-24 text-center">
          <a 
            href="https://www.paduaweddings.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-[10px] tracking-[0.4em] uppercase text-brand-espresso/40 hover:text-brand-gold transition-colors duration-300"
          >
            Return to Venue Site &mdash;
          </a>
        </div>
      </section>

      <GalleryLightbox 
        images={galleryImages}
        currentIndex={lightbox.index}
        isOpen={lightbox.isOpen}
        onClose={closeLightbox}
        onNavigate={navigateLightbox}
      />
    </div>
  );
};

export default GalleriesIndex;